import { useMutation } from '@apollo/client';
import { Button, Col, DatePicker, Form, Input, Modal, Row, Spin } from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import React, { useState } from 'react';
import { LIMIT, REGEX } from '../../../common/constants';
import CommonSelect from '../../../components/CommonSelect';
import ModalRouterPrompt from '../../../components/ModalClosePrompt';
import { ADD_RELEASE } from '../graphql/Mutations';
import { PROJECT_FOR_RELEASE } from '../graphql/Queries';

const ReleaseModal = (props) => {
  const { show, close, executeQuery, clearFiltersAfterCreate } = props;
  const [form] = Form?.useForm();
  const [isFormValueChange, setIsFormValueChange] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [loading, setLoading] = useState(false);

  const onCancel = () => {
    if (isFormValueChange) {
      setShowPrompt(true);
    } else {
      form?.resetFields();
      close();
    }
  };

  const [initialFilter, setInitialFilter] = useState({
    skip: 0,
    searchString: '',
    sortOrder: 'DESC',
    sortField: 'createdAt',
  });
  const [projectSearchString, setProjectSearchString] = useState('');
  const [selectedProject, setSelectedProject] = useState(undefined);

  const handleProjectSearch = debounce((value) => {
    setProjectSearchString(value?.toLowerCase());
  }, 500);

  const handleProjectSelect = (value) => {
    setSelectedProject(value);
    setProjectSearchString('');
  };

  const handleSearch = (value) => {
    const filter = {
      ...initialFilter,
      skip: 0,
      searchString: value?.trim(),
    };
    setInitialFilter(filter);
    executeQuery({
      variables: {
        filters: {
          ...filter,
          limit: LIMIT,
        },
      },
    });
  };

  const [addRelease] = useMutation(ADD_RELEASE, {
    onCompleted: () => {
      setLoading(false);
      handleSearch();
      clearFiltersAfterCreate?.();
      close();
      form?.resetFields();
    },
    onError: () => {
      setLoading(false);
    },
  });

  const onFinish = (values) => {
    setLoading(true);
    addRelease({
      variables: {
        input: {
          releaseNumber: values?.releaseNumber,
          description: values?.description,
          releaseDate: values?.releaseDate
            ? values?.releaseDate?.toISOString()
            : null,
          projectId: values?.projectId?.value,
          projectName: values?.projectId?.label,
        },
      },
    });
  };

  const disabledDate = (current) =>
    current && current?.isBefore(dayjs()?.startOf('day'));

  return (
    <>
      <Modal
        title="Create Release"
        open={show}
        onCancel={onCancel}
        footer={null}
        centered
        destroyOnClose
      >
        <Spin spinning={loading}>
          <div className="form mt-32">
            <Form
              name="control-ref"
              form={form}
              onFinish={onFinish}
              layout="vertical"
              onFieldsChange={() => {
                setIsFormValueChange(true);
              }}
            >
              <Row gutter={[8, 0]}>
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="releaseNumber"
                    label="Release Number:"
                    rules={[
                      {
                        required: true,
                        message: 'Please Input Release Number!',
                      },
                      {
                        pattern: REGEX.RELEASE_NUMBER,
                        message:
                          'Please enter a valid Release Number! (e.g., REL-1.0.0 or 1.0.0+123)',
                      },
                    ]}
                    extra="Format examples: REL-1.0.0"
                  >
                    <Input placeholder="Enter Release Number" />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="projectId"
                    label="Project:"
                    rules={[
                      { required: true, message: 'Please Select Project!' },
                    ]}
                  >
                    <CommonSelect
                      placeholder="Select Project"
                      allowClear
                      labelInValue
                      query={PROJECT_FOR_RELEASE}
                      onSearch={handleProjectSearch}
                      onChange={handleProjectSelect}
                      responsePath="projectsForRelease.projects"
                      valuePath="id"
                      labelPath="name"
                      fetchPolicy="network-only"
                      value={selectedProject}
                      variables={{
                        filters: {
                          searchString: projectSearchString,
                          sortOrder: 'DESC',
                          sortField: 'createdOn',
                        },
                      }}
                      useEffectDeps={[projectSearchString]}
                      showSearch
                      virtual={false}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="releaseDate"
                    label="Release Date:"
                    rules={[{ required: true, message: 'Please Select Date!' }]}
                  >
                    <DatePicker
                      className="w-100"
                      disabledDate={disabledDate}
                      placeholder="Select Date"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="description" label="Description:">
                    <Input.TextArea rows={4} placeholder="Enter Description" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                wrapperCol={{
                  offset: 9,
                }}
              >
                <Button type="primary" htmlType="submit" disabled={loading}>
                  Add Release
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Spin>
        <ModalRouterPrompt
          open={showPrompt}
          handleOK={() => {
            setShowPrompt(false);
            setIsFormValueChange(false);
            form?.resetFields();
            close();
          }}
          handleCancel={() => setShowPrompt(false)}
        />
      </Modal>
    </>
  );
};

export default ReleaseModal;
